// The prize list: the Standings page's third view, and the editor behind it.
//
// Who wins the junior prize is a question this app could not answer until now. The answer
// is in the standings already — it is the highest-placed junior — but "junior" had to be
// written down first, and then somebody had to read down the table with a finger. This
// view writes it down once and reads the table every time.
//
// The rules live in js/utils/categories.js; this file reads inputs, writes inputs, and
// shows or hides things.

import { state, saveToLocalStorage, getActiveSettings, countedRoundsFor, writeActiveSetting } from '../state.js';
import { formatPlayerName, escapeHtml } from '../utils/formatting.js';
import {
    categoryStandings,
    describeCategory,
    normalizeCategory,
    normalizeCategories,
    suggestedCategories,
    DEFAULT_PLACES,
    MAX_PLACES
} from '../utils/categories.js';
import { registerStandingsView, refreshActiveStandingsView } from './standings-views.js';

const ROUND_ROBIN_FORMATS = [
    'rr', 'drr', 'round-robin', 'roundrobin', 'double-rr', 'double-round-robin'
];

// The editor's working copy. Edits are applied on Save, not as they are typed, so an
// arbiter who opens the editor to look at it can close it again without having changed
// the prize list of a tournament that is running.
let draft = [];

// WHICH group's categories the editor is working on.
//
// Null is the ordinary case: the ACTIVE group, whose prize list is on the Standings page
// behind the editor. A group id is the other caller — the create/edit modal's per-group
// settings, where the group being edited is often NOT the one that is open, and writing
// to the active group there would put one group's prizes on another.
let editingGroupId = null;
let onEditorSaved = null;

/**
 * The year the age categories are measured in: the tournament's own, not today's.
 *
 * It matters when the prize list is read back months later — a junior prize belongs to
 * the year the event was played, and re-opening a finished tournament in January must not
 * quietly move a twelve-year-old up a class. The Statistics window on the Players page
 * (js/ui/player-stats.js) measures ages in the same year, so its age bands and these
 * prizes hold the same players.
 */
export function tournamentYear() {
    const t = state.activeTournament || {};
    const iso = String(t.startDate || '').trim();
    const m = /^(\d{4})-\d{2}-\d{2}/.exec(iso);
    return m ? Number(m[1]) : new Date().getFullYear();
}

/** The ranked order the standings screen produced, for the same tournament settings. */
function rankedOrder() {
    const settings = getActiveSettings();
    const isRR = ROUND_ROBIN_FORMATS.includes(settings.format);
    return window.TieBreak.compute(state.players, state.rounds, {
        pointSystem: settings.pointSystem,
        tiebreaks: settings.tiebreaks,
        totalRounds: parseInt(settings.roundsCount, 10) || state.rounds.length,
        isRoundRobin: isRR,
        countedRounds: countedRoundsFor(settings)
    }).order;
}

function currentCategories() {
    return normalizeCategories(getActiveSettings().categories);
}

/** One group of the tournament being edited, by id. */
function groupById(id) {
    const groups = (state.activeTournament || {}).groups;
    if (!Array.isArray(groups)) return null;
    return groups.find(g => String(g.id) === String(id)) || null;
}

/** The categories the editor is currently working on — see editingGroupId. */
function categoriesBeingEdited() {
    if (!editingGroupId) return currentCategories();
    const group = groupById(editingGroupId);
    return normalizeCategories(group && group.categories);
}

/** Write them back where they came from. Returns false when the target is gone. */
function storeEditedCategories(list) {
    if (!editingGroupId) return writeActiveSetting('categories', list);
    const group = groupById(editingGroupId);
    if (!group) return false;
    group.categories = list;
    return true;
}

/**
 * "8 categories" — what the per-group settings show beside their Edit button, so an
 * arbiter can see which groups have prizes without opening each one.
 *
 * The dictionary has no plural rules, so the key is picked rather than the sentence
 * assembled; same approach as the findings in js/utils/preflight.js.
 */
export function categorySummaryText(group) {
    const n = normalizeCategories(group && group.categories).length;
    if (n === 0) return tr('No prize categories');
    return n === 1 ? tr('One category') : tr('{n} categories', { n });
}

/** The prize list for the active tournament. `all` lists every member, not just winners. */
function currentPrizes(all) {
    return categoryStandings({
        order: rankedOrder(),
        players: state.players,
        categories: currentCategories(),
        year: tournamentYear(),
        all
    });
}

/** "under 13, women" — the constraints as one readable line. */
function describeLine(category) {
    const parts = describeCategory(category).map(p => tr(p.key, p.params));
    return parts.join(', ');
}

// ---- the view --------------------------------------------------------------

function emptyStateHtml() {
    const canEdit = !state.isSpectatorMode;
    return `
        <div class="empty-state-container" style="min-height: 30vh;">
            <div class="empty-state-content glass" style="padding: 2rem;">
                <i class="ri-award-line" style="font-size: 3rem; color: var(--primary); margin-bottom: 1rem;"></i>
                <h3>${tr('No prize categories yet')}</h3>
                <p>${tr('Add a category — best junior, best woman, U12 — and the highest-placed player in it wins it.')}</p>
                ${canEdit ? `<button type="button" class="btn-primary" id="btn-prizes-empty-add" style="margin-top: 1rem;">
                    <i class="ri-add-line"></i> ${tr('Add Categories')}
                </button>` : ''}
            </div>
        </div>`;
}

function prizeTableHtml(result) {
    const { category, rows, total } = result;
    const description = describeLine(category);

    const body = rows.length === 0
        ? `<tr><td colspan="4" style="padding: 1rem; color: var(--text-muted);">${tr('No players in this category.')}</td></tr>`
        : rows.map(row => {
            const player = state.players.find(p => String(p.id) === String(row.id));
            return `
                <tr data-player-id="${escapeHtml(row.id)}">
                    <td style="width: 50px;"><strong>${row.place}</strong></td>
                    <td style="width: 70px; color: var(--text-muted);">${row.rank}</td>
                    <td><div class="tbl-title clickable-player" data-id="${escapeHtml(row.id)}"
                        style="cursor: pointer; color: var(--primary);"
                        title="${escapeHtml(formatPlayerName(player, false))}">${formatPlayerName(player, true)}</div></td>
                    <td style="width: 80px;"><span class="points-value">${row.points}</span></td>
                </tr>`;
        }).join('');

    return `
        <div class="card glass" style="margin-bottom: 1.25rem;">
            <div class="card-header" style="padding: 0.9rem 1.25rem;">
                <h3 style="margin: 0; font-size: 1rem;">
                    <i class="ri-award-line"></i> ${escapeHtml(category.name)}
                </h3>
                <span style="color: var(--text-muted); font-size: 0.85rem;">
                    ${description ? escapeHtml(description) + ' • ' : ''}${tr('{n} players', { n: total })}
                </span>
            </div>
            <div class="card-body" style="padding: 0;">
                <div class="table-responsive">
                    <table class="data-table compact-table">
                        <thead>
                            <tr>
                                <th style="width: 50px;">${tr('Place')}</th>
                                <!-- Not tr('Rank'): the Norwegian dictionary renders that as
                                     "Plass" too, and two adjacent columns headed the same
                                     word is worse than no heading at all. -->
                                <th style="width: 70px;" title="${escapeHtml(tr('Place in the tournament'))}">${tr('Overall')}</th>
                                <th>${tr('Player')}</th>
                                <th style="width: 80px;">${tr('Points')}</th>
                            </tr>
                        </thead>
                        <tbody>${body}</tbody>
                    </table>
                </div>
            </div>
        </div>`;
}

export function renderPrizes() {
    const container = document.getElementById('prizes-body');
    if (!container) return;

    const categories = currentCategories();
    if (categories.length === 0) {
        container.innerHTML = emptyStateHtml();
        // Called with no arguments on purpose: a listener passed straight through would
        // hand the click Event in as the options object.
        const add = document.getElementById('btn-prizes-empty-add');
        if (add) add.addEventListener('click', () => openCategoryEditor());
        return;
    }

    // Every member is listed on screen, not only the prize places: an arbiter checking a
    // category wants to see who is in it, and the places are marked by their numbering
    // anyway. The PRINTED sheet is the one that stops at the prizes.
    container.innerHTML = currentPrizes(true).map(prizeTableHtml).join('');
}

// ---- the editor ------------------------------------------------------------

const modal = () => document.getElementById('prize-categories-modal');

function draftRowHtml(category, index) {
    const num = (value) => (value === null || value === undefined ? '' : value);
    return `
        <tr data-index="${index}">
            <td><input type="text" class="cat-field" data-field="name" value="${escapeHtml(category.name)}"
                placeholder="${escapeHtml(tr('Category name'))}" style="min-width: 150px;"></td>
            <td><div style="display: flex; gap: 0.25rem; align-items: center;">
                <input type="number" class="cat-field" data-field="ageMin" value="${num(category.ageMin)}" min="0" max="120" style="width: 62px;" placeholder="–">
                <span style="color: var(--text-muted);">–</span>
                <input type="number" class="cat-field" data-field="ageMax" value="${num(category.ageMax)}" min="0" max="120" style="width: 62px;" placeholder="–">
            </div></td>
            <td><div style="display: flex; gap: 0.25rem; align-items: center;">
                <input type="number" class="cat-field" data-field="ratingMin" value="${num(category.ratingMin)}" min="0" max="4000" step="50" style="width: 76px;" placeholder="–">
                <span style="color: var(--text-muted);">–</span>
                <input type="number" class="cat-field" data-field="ratingMax" value="${num(category.ratingMax)}" min="0" max="4000" step="50" style="width: 76px;" placeholder="–">
            </div></td>
            <td><select class="cat-field" data-field="sex" style="width: 110px;">
                <option value="" ${category.sex === '' ? 'selected' : ''}>${tr('Anyone')}</option>
                <option value="w" ${category.sex === 'w' ? 'selected' : ''}>${tr('Women')}</option>
                <option value="m" ${category.sex === 'm' ? 'selected' : ''}>${tr('Men')}</option>
            </select></td>
            <td style="text-align: center;">
                <input type="checkbox" class="cat-field" data-field="unratedOnly" ${category.unratedOnly ? 'checked' : ''}>
            </td>
            <td><input type="number" class="cat-field" data-field="places" value="${category.places}" min="1" max="${MAX_PLACES}" style="width: 62px;"></td>
            <td style="text-align: right;">
                <button type="button" class="btn-icon-small btn-icon-danger cat-remove" data-index="${index}"
                    title="${escapeHtml(tr('Remove'))}"><i class="ri-delete-bin-line"></i></button>
            </td>
        </tr>`;
}

function renderDraft() {
    const body = document.getElementById('prize-categories-list');
    if (!body) return;

    body.innerHTML = draft.length
        ? draft.map(draftRowHtml).join('')
        : `<tr><td colspan="7" style="text-align: center; padding: 2rem; color: var(--text-muted);">${tr('No categories yet.')}</td></tr>`;

    body.querySelectorAll('.cat-field').forEach(el => {
        el.addEventListener('change', () => {
            const index = Number(el.closest('tr').dataset.index);
            const field = el.dataset.field;
            const value = el.type === 'checkbox' ? el.checked : el.value;
            // Re-normalised on every edit, so a blank box becomes null rather than '' and
            // the row on screen is always a category the rest of the app would accept.
            draft[index] = normalizeCategory({ ...draft[index], [field]: value });
        });
    });

    body.querySelectorAll('.cat-remove').forEach(btn => {
        btn.addEventListener('click', () => {
            draft.splice(Number(btn.dataset.index), 1);
            renderDraft();
        });
    });
}

function addBlankCategory() {
    draft.push(normalizeCategory({ name: '', places: DEFAULT_PLACES }));
    renderDraft();
    // Straight into the name box: a nameless category is dropped on save, so the one
    // thing this row needs is the one thing the cursor should be in.
    const last = document.querySelectorAll('#prize-categories-list input[data-field="name"]');
    if (last.length) last[last.length - 1].focus();
}

function addSuggestedCategories() {
    const existing = new Set(draft.map(c => c.name.toLowerCase()));
    suggestedCategories().forEach(c => {
        const translated = { ...c, name: tr(c.name) };
        if (!existing.has(translated.name.toLowerCase())) draft.push(normalizeCategory(translated));
    });
    renderDraft();
}

/**
 * @param {Object} [options]
 * @param {string} [options.groupId] edit this group's categories rather than the active
 *                                   group's — see editingGroupId
 * @param {Function} [options.onSaved] called after a successful save, for a caller that
 *                                     shows its own summary of them
 */
export function openCategoryEditor(options = {}) {
    if (state.isSpectatorMode || !state.activeTournament) return;
    editingGroupId = options.groupId || null;
    onEditorSaved = typeof options.onSaved === 'function' ? options.onSaved : null;
    draft = categoriesBeingEdited();
    renderDraft();
    const m = modal();
    if (m) m.classList.add('active');
}

function saveCategories() {
    // Nameless rows are dropped rather than refused: an arbiter who added a row and
    // changed their mind should be able to press Save, not hunt for the delete button.
    const kept = normalizeCategories(draft);
    if (!storeEditedCategories(kept)) return;
    saveToLocalStorage();
    const m = modal();
    if (m) m.classList.remove('active');

    const notify = onEditorSaved;
    editingGroupId = null;
    onEditorSaved = null;

    // Both are safe whichever group was edited: they redraw from the ACTIVE group, so
    // editing another group's prizes simply leaves the page as it was.
    renderPrizes();
    refreshActiveStandingsView();
    if (notify) notify();
}

// ---- print -----------------------------------------------------------------

/**
 * The prize list as a printed sheet — the one that goes up on the wall at the end.
 *
 * Unlike the screen, this stops at each category's prize places. On screen an arbiter is
 * checking who is in a category; on paper everyone is looking for who won it.
 */
export function prizesPrintHtml() {
    const results = currentPrizes(false);
    if (results.length === 0) {
        return `<p class="text-center" style="padding: 2rem;">${tr('No prize categories have been set up.')}</p>`;
    }

    const blocks = results.map(({ category, rows, total }) => {
        const description = describeLine(category);
        const body = rows.length === 0
            ? `<tr><td colspan="4">${tr('No players in this category.')}</td></tr>`
            : rows.map(row => {
                const player = state.players.find(p => String(p.id) === String(row.id));
                return `
                    <tr>
                        <td class="text-center font-bold">${row.place}</td>
                        <td class="text-center">${row.rank}</td>
                        <td>${escapeHtml(formatPlayerName(player))}</td>
                        <td class="text-right font-bold">${row.points}</td>
                    </tr>`;
            }).join('');

        return `
            <h3 style="margin: 1.25rem 0 0.35rem;">${escapeHtml(category.name)}</h3>
            ${description || total ? `<p style="margin: 0 0 0.35rem; font-size: 9pt;">${
                description ? escapeHtml(description) + ' — ' : ''}${tr('{n} players', { n: total })}</p>` : ''}
            <table class="print-table">
                <thead>
                    <tr>
                        <th style="width: 40pt;" class="text-center">${tr('Place')}</th>
                        <th style="width: 40pt;" class="text-center">${tr('Overall')}</th>
                        <th>${tr('Player')}</th>
                        <th style="width: 40pt;" class="text-right">${tr('Pts')}</th>
                    </tr>
                </thead>
                <tbody>${body}</tbody>
            </table>`;
    }).join('');

    return `
        <h2 class="text-center" style="margin-bottom: 1rem;">${tr('Prize List')}</h2>
        ${blocks}
        <div style="font-size: 9pt; margin-top: 1rem;">
            <p>${tr('“Overall” is the player’s place in the tournament as a whole.')}</p>
        </div>`;
}

// ---- wiring ----------------------------------------------------------------

registerStandingsView('prizes', {
    wrapId: 'prizes-wrap',
    buttonIds: ['btn-print-prizes', 'btn-edit-categories'],
    render: renderPrizes
});

export function setupPrizes() {
    const open = document.getElementById('btn-edit-categories');
    if (open) open.addEventListener('click', () => openCategoryEditor());

    // Cancelling leaves no target behind. Nothing can act on a stale one — a save only
    // ever follows an open, which sets it — but a value that outlives its modal is the
    // kind of thing the next reader has to prove harmless.
    const m = modal();
    if (m) {
        m.querySelectorAll('.btn-close-modal').forEach(btn => {
            btn.addEventListener('click', () => { editingGroupId = null; onEditorSaved = null; });
        });
    }

    const add = document.getElementById('btn-add-category');
    if (add) add.addEventListener('click', addBlankCategory);

    const suggest = document.getElementById('btn-add-suggested-categories');
    if (suggest) suggest.addEventListener('click', addSuggestedCategories);

    const save = document.getElementById('btn-save-categories');
    if (save) save.addEventListener('click', saveCategories);
}
